import {
	Alert,
	AlertIcon,
	Badge,
	Button,
	Checkbox,
	HStack,
	Input,
	InputGroup,
	InputRightElement,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Progress,
	Select,
	Stack,
	Text,
	useToast,
	VStack
} from '@chakra-ui/react';
import { Copy, ExternalLink, Trash2 } from 'lucide-react';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useContext, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { type Blueprint, COUNTERTOP_INVALID_KEY, type Kitchen } from '@rta/rta-blueprint-library';
import { useHeadlessCapture } from '@rta/rta-blueprint-3d-component';
import { useHeadlessOverheadCapture } from '@rta/rta-blueprint-component';

import { AppContext, DesignActionTypes, type Controller } from '@context';
import {
	ContentPanelMode,
	customerState,
	projectState,
	selectContentPanelMode,
	selectGeneratePresentationModal,
	selectNavigation,
	setContentPanelMode,
	setGeneratePresentationModal,
	useAppDispatch,
	useAppSelector
} from '@state';
import { pdf } from '@react-pdf/renderer';

import { buildPresentationEstimate, createPresentation, deleteDesignPackage, fetchDesignPackageLinks, fetchDesignPriceBook, patchBlueprintMetadata, patchPricePin, saveCustomerBlueprint, uploadPresentationDesign, warmOptimizeWorker, type PresentationDesignDetailsPayload, type UploadPhase } from '@external';
import { DepositLinkBadge } from '../ui/deposit-link-badge';
import { EstimateDocument } from '../documents';
import { getDesignImage } from '@helpers';
import { lang } from '@lang';

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const buildDetails = (kitchen: Kitchen): PresentationDesignDetailsPayload => {
	const { material, itemizedMsrp: msrp } = kitchen;
	const uniqueAppliances = [...new Set(msrp.items.map((li) => li.description))];
	return {
		islandSize: kitchen.description,
		islandFinish: material.surround.displayName,
		countertop: material.countertop.displayName,
		appliances: uniqueAppliances
	};
};

type Step = 'select' | 'capture' | 'success' | 'partial';

type DesignOutcome = {
	designId: string;
	name: string;
	glbOk: boolean;
	uploadOk: boolean;
	error?: string;
};

type GeneratedLink = {
	designId: string;
	name: string;
	link: string;
};

/**
 * Override shape accepted by `handleGenerate` and by the dev-bridge
 * `window.__devBridge.generatePresentation` entry point. Each field
 * defaults to the modal's current local UI state when omitted, so a
 * dev caller can either let the modal's selections drive the run or
 * pass explicit values without first populating the form.
 */
type GenerateOverrides = {
	dealId?: string;
	designIds?: Array<string>;
	customNames?: Record<string, string>;
	expirationDate?: string;
};

const isNonLive = import.meta.env.VITE_APP_ENVIRONMENT !== 'LIVE';

type ExpirationPreset = {
	key: string;
	label: string;
	days?: number;
	months?: number;
	years?: number;
	testOnly?: boolean;
};

const EXPIRATION_PRESETS: Array<ExpirationPreset> = [
	{ key: '30d', label: '30 days', days: 30 },
	{ key: '6m', label: '6 months', months: 6 },
	{ key: '1y', label: '1 year', years: 1 },
	{ key: 'expired', label: 'Already expired (test only)', days: -1, testOnly: true }
];

const presetToDate = (preset: ExpirationPreset): string => {
	const { days = 0, months = 0, years = 0 } = preset;
	const date = new Date();
	date.setFullYear(date.getFullYear() + years, date.getMonth() + months, date.getDate() + days);
	return date.toISOString().slice(0, 10);
};

const TEARDOWN_POLL_INTERVAL_MS = 50;
const TEARDOWN_MAX_WAIT_MS = 2000;
const CAPTURE_MAX_ATTEMPTS = 3;

// Bar weights mirror measured wall time: link/live-export/teardown ~8%, sequential 3D captures ~50%, upload+document chains ~42%
const PROGRESS_PROLOGUE = 8;
const PROGRESS_CAPTURE_TOTAL = 50;
const PROGRESS_UPLOAD_TOTAL = 42;
const UPLOAD_PHASE_SPLIT: Record<UploadPhase, number> = { glb: 0.45, main: 0.15, overhead: 0.2, estimate: 0.2 };
const PROGRESS_TRICKLE_INTERVAL_MS = 300;
const PROGRESS_TRICKLE_RATE = 0.08;

export const GeneratePresentationModal = (): JSX.Element => {
	const dispatch = useAppDispatch();
	const { getToken } = useKeystoneSession();
	const toast = useToast();

	const state = useAppSelector(selectGeneratePresentationModal);
	const appNav = useAppSelector(selectNavigation);
	const contentMode = useAppSelector(selectContentPanelMode);
	const customer = useAppSelector(customerState);
	const project = useAppSelector(projectState);
	const { appState: { customerDesigns: designs }, appDispatch } = useContext(AppContext);

	const { captureDesign, CapturePortal } = useHeadlessCapture({
		assetsBaseUrl: import.meta.env.VITE_ASSETS_BASE_URL
	});

	const { captureOverhead, CapturePortal: CaptureOverheadPortal } = useHeadlessOverheadCapture({
		concurrency: 3,
		assetsBaseUrl: import.meta.env.VITE_ASSETS_BASE_URL
	});

	// Sort designs by submissionDate descending to match the tab bar order
	const sortedDesigns = useMemo(() =>
		[...designs.designs].sort((a, b) => Number(b.blueprint.submissionDate) - Number(a.blueprint.submissionDate)),
	[designs.designs]);

	const [step, setStep] = useState<Step>('select');
	const [selectedDealId, setSelectedDealId] = useState<string>('');
	const [selectedDesignIds, setSelectedDesignIds] = useState<Array<string>>([]);

	// Default the deal selection to match getEstimatedTax's fallback (kitchen.dealId, then first
	// deal) so the package tax equals the in-tool estimate tax unless the dealer explicitly changes it.
	useEffect(() => {
		if (state.isOpen && !selectedDealId && project.length > 0) {
			const activeKitchenDealId = appNav.designId
				? designs.designs.find((d) => d.blueprint.id === appNav.designId)?.bpController?.kitchen?.dealId
				: undefined;
			const defaultDeal = project.find((p) => p.dealId === activeKitchenDealId) ?? project[0];
			setSelectedDealId(defaultDeal.dealId);
		}
	}, [state.isOpen, project, selectedDealId]);
	const [customNames, setCustomNames] = useState<Record<string, string>>({});
	const [expirationKey, setExpirationKey] = useState<string>(EXPIRATION_PRESETS[0].key);
	const [captureProgress, setCaptureProgress] = useState(0);
	const [captureStatus, setCaptureStatus] = useState('');
	const [presentationLink, setPresentationLink] = useState('');
	const [generatedLinks, setGeneratedLinks] = useState<Array<GeneratedLink>>([]);
	const [failedDesigns, setFailedDesigns] = useState<Array<DesignOutcome>>([]);
	const activePresentationIdRef = useRef<string>('');

	// Only the legacy createPresentation flow writes an actual package record — the self-contained
	// link flow (handleGenerateLinks) writes nothing, so there is no package id to delete for it.
	const [createdPackageId, setCreatedPackageId] = useState<string | null>(null);
	const [isPackageDeleted, setIsPackageDeleted] = useState(false);
	const [isDeletingPackage, setIsDeletingPackage] = useState(false);
	const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	// Progress ledger: granted = completed work (hard floor), ceiling = granted + in-flight allocation; the trickle eases toward the ceiling without overstating work in flight
	const progressGrantedRef = useRef(0);
	const progressCeilingRef = useRef(0);
	const captureLoopActiveRef = useRef(false);

	const resetProgress = (): void => {
		progressGrantedRef.current = 0;
		progressCeilingRef.current = 0;
		setCaptureProgress(0);
	};

	const grantProgress = (units: number): void => {
		progressGrantedRef.current = Math.min(100, progressGrantedRef.current + units);
		if (progressCeilingRef.current < progressGrantedRef.current) progressCeilingRef.current = progressGrantedRef.current;
		setCaptureProgress((prev) => Math.max(prev, Math.round(progressGrantedRef.current * 10) / 10));
	};

	const beginProgressStep = (allocation: number): void => {
		progressCeilingRef.current = Math.min(99, progressGrantedRef.current + allocation);
	};

	useEffect(() => {
		if (step !== 'capture') return;
		const interval = setInterval(() => {
			setCaptureProgress((prev) => {
				const ceiling = progressCeilingRef.current;
				if (prev >= ceiling - 0.2) return prev;
				return Math.round((prev + (ceiling - prev) * PROGRESS_TRICKLE_RATE) * 10) / 10;
			});
		}, PROGRESS_TRICKLE_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [step]);

	const onClose = (): void => {
		dispatch(setGeneratePresentationModal(false));
		setStep('select');
		setSelectedDealId('');
		setSelectedDesignIds([]);
		setCustomNames({});
		setExpirationKey(EXPIRATION_PRESETS[0].key);
		resetProgress();
		setCaptureStatus('');
		setPresentationLink('');
		setGeneratedLinks([]);
		setFailedDesigns([]);
		activePresentationIdRef.current = '';
		setCreatedPackageId(null);
		setIsPackageDeleted(false);
		setIsDeletingPackage(false);
		setDeleteConfirmOpen(false);
	};

	type DesignContext = { designId: string; blueprint: Blueprint | null; kitchen: Kitchen | null; customName: string };

	const runDesigns = async (params: {
		presentationId: string;
		designContexts: Array<DesignContext>;
		activeDesignId: string | null;
		liveGlb: ArrayBuffer | null;
		effectiveDealId: string;
		totalSteps: number;
		progressOffset: number;
	}): Promise<Array<DesignOutcome>> => {
		const { presentationId, designContexts: rawContexts, activeDesignId, liveGlb, effectiveDealId, totalSteps, progressOffset } = params;

		const captureShare = PROGRESS_CAPTURE_TOTAL / totalSteps;
		const uploadShare = PROGRESS_UPLOAD_TOTAL / totalSteps;
		const uploadGrantedByDesign = new Map<string, number>();
		let settledUploads = 0;

		const grantUploadPhase = (designId: string, fraction: number): void => {
			const current = uploadGrantedByDesign.get(designId) ?? 0;
			const target = Math.min(1, current + fraction);
			uploadGrantedByDesign.set(designId, target);
			grantProgress((target - current) * uploadShare);
		};

		const refreshUploadStatus = (): void => {
			if (captureLoopActiveRef.current) return;
			setCaptureStatus(settledUploads >= totalSteps
				? 'Finalizing your presentation...'
				: `Uploading designs & rendering documents — ${settledUploads} of ${totalSteps} complete`);
		};

		// The live design's GLB is already in hand — process it first so its upload
		// starts on the wire while the first headless capture is still building.
		const designContexts = liveGlb
			? [...rawContexts.filter((c) => c.designId === activeDesignId), ...rawContexts.filter((c) => c.designId !== activeDesignId)]
			: rawContexts;

		// Overhead/PDF rendering is CPU-heavy but only gates tail PUTs — hold it until all 3D captures finish so scene builds aren't starved; the aux burst then overlaps GLB uploads already on the wire
		let releaseAuxWork: () => void = () => undefined;
		const auxGate = new Promise<void>((resolve) => { releaseAuxWork = resolve; });

		// Phase A — 2D overhead PNG captures
		const overheadPromises: Array<Promise<string>> = designContexts.map(({ blueprint }) => {
			if (!blueprint) return Promise.resolve('');
			return auxGate
				// scale 1.5 (2400px wide) is still 2x+ for the viewer panel and ~45% fewer pixels to rasterize/encode
				.then(() => captureOverhead({ blueprint, scale: 1.5 }).then((r) => r.png))
				.catch(() => '');
		});

		// Phase B — pricing/details compute synchronously (they gate the main PUT);
		// only the slow react-pdf render rides the aux gate.
		const pricingData = designContexts.map(({ kitchen }) => {
			if (!kitchen) return { pricing: undefined, details: undefined };
			try {
				return {
					pricing: buildPresentationEstimate({ kitchen, customer, project, dealId: effectiveDealId }),
					details: buildDetails(kitchen)
				};
			} catch {
				return { pricing: undefined, details: undefined };
			}
		});
		const estimatePdfPromises: Array<Promise<Blob | null>> = designContexts.map(async ({ kitchen }, i) => {
			await auxGate;
			const { pricing } = pricingData[i];
			if (!kitchen || !pricing) return null;
			try {
				return await pdf(
					<EstimateDocument data={{ customer, project, tax: pricing.tax, kitchen, image: '' }} />
				).toBlob();
			} catch {
				return null;
			}
		});

		// Phase C — sequential 3D capture, uploads kicked off as each finishes
		const uploadSettlements: Array<Promise<DesignOutcome>> = [];
		captureLoopActiveRef.current = true;

		for (let i = 0; i < designContexts.length; i++) {
			const { designId, blueprint, customName } = designContexts[i];
			const absoluteIndex = progressOffset + i;

			setCaptureStatus(`Building 3D model ${absoluteIndex + 1} of ${totalSteps}: ${customName}`);
			beginProgressStep(captureShare);

			let glb: ArrayBuffer | null = null;
			let glbOk = false;
			let captureError: string | undefined;

			if (blueprint) {
				if (designId === activeDesignId && liveGlb) {
					glb = liveGlb;
					glbOk = true;
				} else {
					for (let attempt = 0; attempt < CAPTURE_MAX_ATTEMPTS; attempt++) {
						try {
							const result = await captureDesign({ blueprint, glbOnly: true, designId });
							if (result.error) {
								captureError = result.error;
							} else {
								glb = result.glb;
								glbOk = true;
								captureError = undefined;
								break;
							}
						} catch (err) {
							captureError = err instanceof Error ? err.message : String(err);
						}
						console.warn(`[GeneratePresentation] capture attempt ${attempt + 1}/${CAPTURE_MAX_ATTEMPTS} failed for ${designId}: ${captureError}`);
						// Cool-down so the failed attempt's renderer teardown fully settles before the next mount
						await sleep(750);
					}
				}
			} else {
				glbOk = true; // no blueprint — nothing to capture, not a capture failure
			}

			grantProgress(captureShare);

			const uploadSettlement = (async (): Promise<DesignOutcome> => {
				const glbMb = glb ? (glb.byteLength / 1048576).toFixed(1) : null;
				if (captureLoopActiveRef.current) {
					setCaptureStatus(glbMb ? `Uploading ${customName} (3D model ${glbMb} MB)...` : `Uploading ${customName}...`);
				}
				// GLB streams first; the estimate/overhead promises gate only their own PUTs
				const [_result, uploadErr] = await uploadPresentationDesign({
					presentationId,
					designId,
					designName: customName,
					pricing: pricingData[i].pricing,
					details: pricingData[i].details,
					glb: captureError ? null : glb,
					estimatePdf: estimatePdfPromises[i],
					overheadPng: overheadPromises[i],
					onPhase: (phase) => grantUploadPhase(designId, UPLOAD_PHASE_SPLIT[phase]),
					getAccessTokenSilently: getToken
				});
				// True up whatever the chain skipped (no glb, failed aux, legacy fallbacks) so the bar can't strand
				grantUploadPhase(designId, 1);
				settledUploads++;
				refreshUploadStatus();

				if (captureError) {
					return { designId, name: customName, glbOk: false, uploadOk: !uploadErr, error: captureError };
				}
				return {
					designId,
					name: customName,
					glbOk,
					uploadOk: !uploadErr,
					error: uploadErr ?? undefined
				};
			})();

			uploadSettlements.push(uploadSettlement);
		}
		captureLoopActiveRef.current = false;
		releaseAuxWork();

		// Everything left is now in flight — let the trickle ride the remaining upload allocation
		beginProgressStep(100);
		refreshUploadStatus();
		return Promise.all(uploadSettlements);
	};

	const resolveKitchen = (designId: string): Kitchen | null => {
		const designState = designs.designs.find((d) => d.blueprint.id === designId);
		return designState?.bpController?.kitchen ?? designState?.blueprint?.kitchens?.getActiveVersion() ?? null;
	};

	// The package reads deposit_link from the design's own deal first and falls back to any other deal
	// that has one, so the "Pay Deposit" button only breaks when no deal on the contact carries a link.
	// Either way this is the CRM's state, not a design fault — it warns rather than blocks.
	const resolveDepositLinkWarning = (dealId: string): string | null => {
		const selectedDeal = project.find((deal) => deal.dealId === dealId);
		// An API build predating depositLink reports undefined — unknown, so never assert an absence.
		if (selectedDeal && selectedDeal.depositLink === undefined) return null;
		if (selectedDeal?.depositLink) return null;
		const { depositLinkFallbackOnGenerate, depositLinkMissingOnGenerate } = lang.crm.message;
		return project.some((deal) => deal.depositLink) ? depositLinkFallbackOnGenerate : depositLinkMissingOnGenerate;
	};

	const selectedDealDepositWarning = resolveDepositLinkWarning(selectedDealId);

	// Pricing gate: unresolved countertop materials price at $0, and a failed estimate build means
	// the package would not match the in-tool estimate — block and name the designs either way
	const collectPricingBlockers = (designIds: Array<string>, dealId: string, names: Record<string, string>): Array<string> =>
		designIds.flatMap((designId) => {
			const kitchen = resolveKitchen(designId);
			if (!kitchen) return [];
			const name = names[designId]?.trim() || kitchen.description || designId;
			if ((kitchen.design?.islands?.children?.length ?? 0) === 0) return [`${name}: design has no islands — nothing to present`];
			const countertops = kitchen.design?.countertops?.children ?? [];
			const hasUnresolved = kitchen.material?.countertop?.key === COUNTERTOP_INVALID_KEY
				|| countertops.some((ct) => ct.material?.key === COUNTERTOP_INVALID_KEY);
			if (hasUnresolved) return [`${name}: countertop material could not be resolved — re-select it and save`];
			try {
				buildPresentationEstimate({ kitchen, customer, project, dealId });
				return [];
			} catch (err) {
				return [`${name}: ${err instanceof Error ? err.message : String(err)}`];
			}
		});

	/**
	 * Locks every selected design to the price book it is being generated against, BEFORE anything is
	 * saved or any link is minted. A package re-prices on every page load, so without this the link
	 * silently follows whatever becomes current, and an already-sent package would move again the next
	 * time somebody edited or deleted the targeting rule behind it.
	 *
	 * A design that already carries a pin is never silently re-pinned, and a failure stops generation
	 * rather than emitting a package against pricing nobody locked.
	 * @param designIds the designs about to be generated
	 * @returns true when every design is locked and generation may proceed
	 */
	const pinPriceBooksForGeneration = async (designIds: Array<string>): Promise<boolean> => {
		const customerEmail = appNav.email;
		if (!customerEmail) return true;

		const fail = (message: string): boolean => {
			toast({ title: `Cannot generate — ${message}`, status: 'error', isClosable: true });
			return false;
		};

		for (const designId of designIds) {
			const designState = designs.designs.find((d) => d.blueprint.id === designId);
			const blueprint = designState?.blueprint ?? null;
			const kitchen = designState?.bpController?.kitchen ?? blueprint?.kitchens?.getActiveVersion() ?? null;

			if (!blueprint || !kitchen || kitchen.priceBookVersionId !== null) continue;

			const designName = blueprint.submissionId ?? blueprint.id;
			const label = customNames[designId]?.trim() || kitchen.description || designId;
			const [resolution, resolveError] = await fetchDesignPriceBook({ email: customerEmail, designName, version: kitchen.version, getToken });

			if (resolveError) return fail(`pricing for ${label} could not be resolved`);
			// Not stored yet, or nothing published in this environment: the design keeps following the live
			// catalog exactly as it did before, and there is no version to lock it to.
			if (!resolution?.effectiveVersionId) continue;

			kitchen.priceBookVersionId = resolution.effectiveVersionId;
			kitchen.priceBookPinnedBy = 'presentation';
			kitchen.priceBookRuleId = resolution.effectiveRuleId;

			// An unsaved draft carries the pin into the version the save below mints; anything already in the
			// blob needs the durable write, because a clean design is never re-saved.
			if (kitchen.version === null && designState?.isDirty) continue;

			const [, pinError] = await patchPricePin({
				email: customerEmail,
				designName,
				version: kitchen.version,
				priceBookVersionId: resolution.effectiveVersionId,
				priceBookPinnedBy: 'presentation',
				priceBookRuleId: resolution.effectiveRuleId,
				getToken
			});

			if (pinError) return fail(`pricing for ${label} could not be locked`);
		}

		return true;
	};

	/**
	 * Builds SELF-CONTAINED design-package links: no presentation record, no GLB, no blob/DB/CRM write.
	 * Each link points the package straight at the customer's own design, which the API then reads and
	 * prices live on every open. Dirty designs are saved first (no GLB capture) so the read is never stale.
	 */
	const handleGenerateLinks = async (overrides: GenerateOverrides = {}): Promise<string | null> => {
		const effectiveDealId = overrides.dealId ?? selectedDealId;
		const effectiveDesignIds = overrides.designIds ?? selectedDesignIds;
		const effectiveCustomNames = overrides.customNames ?? customNames;

		if (overrides.dealId && overrides.dealId !== selectedDealId) setSelectedDealId(overrides.dealId);
		if (overrides.designIds) setSelectedDesignIds(overrides.designIds);
		if (overrides.customNames) setCustomNames(overrides.customNames);

		const customerEmail = appNav.email;
		if (effectiveDesignIds.length === 0 || !customerEmail) return null;

		const pricingBlockers = collectPricingBlockers(effectiveDesignIds, effectiveDealId, effectiveCustomNames);
		if (pricingBlockers.length > 0) {
			toast({
				title: `Cannot generate — the package estimate would not match the in-tool pricing. ${pricingBlockers.join('; ')}`,
				status: 'error',
				isClosable: true
			});
			return null;
		}

		if (!await pinPriceBooksForGeneration(effectiveDesignIds)) return null;

		const depositLinkWarning = resolveDepositLinkWarning(effectiveDealId);
		if (depositLinkWarning) toast({ title: depositLinkWarning, status: 'warning', duration: 9000, isClosable: true });

		// Freeze phase: lock each design to an immutable numbered version and attach the existing save-time rendering, so the package's image stays synced to the exact version sent — never whatever the tool becomes later. No live 3D render happens here.
		setStep('capture');
		resetProgress();
		setCaptureProgress(3);

		const lockedVersionByDesignId = new Map<string, number | null>();

		for (let i = 0; i < effectiveDesignIds.length; i++) {
			const designId = effectiveDesignIds[i];
			const designState = designs.designs.find((d) => d.blueprint.id === designId);
			const blueprint = designState?.blueprint ?? null;
			const bpController = designState?.bpController ?? null;
			const kitchen = bpController?.kitchen ?? blueprint?.kitchens?.getActiveVersion() ?? null;
			const snapshotName = effectiveCustomNames[designId]?.trim() || resolveKitchen(designId)?.description || `Design ${i + 1}`;
			const blobId = blueprint?.submissionId ?? designId;

			setCaptureStatus(`Preparing design ${i + 1} of ${effectiveDesignIds.length}: ${snapshotName}`);

			// Reuse the existing save-time rendering — no live 3D re-render — so the version's fallback image stays synced to the blueprint data as-sent.
			const renderingImage = getDesignImage(blueprint) || null;

			// Unsaved edits / a draft get promoted to a new numbered version (the immutable "as-sent" revision); a clean numbered version is reused and only gets its snapshot patched onto it in place.
			const isDirtyOrDraft = Boolean(designState?.isDirty) || (kitchen ? kitchen.version === null : false);
			let lockedVersion: number | null = kitchen?.version ?? null;

			if (kitchen && bpController && isDirtyOrDraft) {
				const numbered = (blueprint?.kitchens?.children ?? []).map((k) => k.version).filter((v): v is number => v !== null);
				const newVersion = numbered.length ? Math.max(...numbered) + 1 : 1;
				const baseVersion = kitchen.version ?? kitchen.baseVersion ?? (newVersion > 1 ? newVersion - 1 : null);
				const [, saveErr] = await saveCustomerBlueprint({
					controller: bpController as unknown as Controller,
					customerEmail,
					mode: 'live',
					getToken,
					kitchenDataOverride: {
						...kitchen.serialize(),
						version: newVersion,
						baseVersion,
						...(renderingImage ? { designPackageImage: renderingImage } : {})
					}
				});
				if (saveErr) {
					toast({ title: `Could not save ${snapshotName} — share link not generated`, status: 'error', isClosable: true });
					setStep('select');
					return null;
				}
				lockedVersion = newVersion;
				bpController.kitchen.isLocal = false;
				appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: designId, isDirty: false } });
			} else if (renderingImage) {
				const [ok, persistErr] = await patchBlueprintMetadata({
					customerEmail,
					designId: blobId,
					metadata: {},
					designPackageImage: renderingImage,
					getToken,
					mode: 'live'
				});
				if (!ok) console.warn(`[GeneratePresentation] snapshot persist failed for ${designId}: ${persistErr}`);
			}

			lockedVersionByDesignId.set(designId, lockedVersion);
			setCaptureProgress(Math.round(((i + 1) / effectiveDesignIds.length) * 90) + 5);
		}

		// One customer-keyed link: each design keyed by the blob id the package reads (submissionId ?? designId), carrying its locked version so the package freezes to the exact version generated.
		const linkDesignsById = new Map<string, { designId: string; version: number | null }>();
		for (const designId of effectiveDesignIds) {
			const blueprint = designs.designs.find((d) => d.blueprint.id === designId)?.blueprint;
			const blobId = blueprint?.submissionId ?? designId;
			if (!linkDesignsById.has(blobId)) linkDesignsById.set(blobId, { designId: blobId, version: lockedVersionByDesignId.get(designId) ?? null });
		}

		const [linkResults, linkError] = await fetchDesignPackageLinks({
			customerEmail,
			designs: [...linkDesignsById.values()],
			dealId: effectiveDealId || undefined,
			getAccessTokenSilently: getToken
		});
		if (linkError || !linkResults) {
			toast({ title: 'Could not generate share links. Please try again.', status: 'error', isClosable: true });
			return null;
		}

		// One combined, customer-keyed link now covers every selected design (buildDesignPackageLinks
		// returns a single element), so there is exactly one URL to share.
		const link = linkResults[0]?.link ?? '';
		setGeneratedLinks([{ designId: 'all', name: '', link }]);
		setPresentationLink(link);
		setStep('success');
		return link || null;
	};

	const handleGenerate = async (overrides: GenerateOverrides = {}): Promise<string | null> => {
		const effectiveDealId = overrides.dealId ?? selectedDealId;
		const effectiveDesignIds = overrides.designIds ?? selectedDesignIds;
		const effectiveCustomNames = overrides.customNames ?? customNames;
		const selectedPreset = EXPIRATION_PRESETS.find((p) => p.key === expirationKey) ?? EXPIRATION_PRESETS[0];
		const effectiveExpirationDate = overrides.expirationDate ?? presetToDate(selectedPreset);

		// Reflect explicit overrides in the visible UI so a dev-bridge run still shows
		// the right selections during the capture step.
		if (overrides.dealId && overrides.dealId !== selectedDealId) setSelectedDealId(overrides.dealId);
		if (overrides.designIds) setSelectedDesignIds(overrides.designIds);
		if (overrides.customNames) setCustomNames(overrides.customNames);

		if (effectiveDesignIds.length === 0 || !appNav.email) return null;

		const pricingBlockers = collectPricingBlockers(effectiveDesignIds, effectiveDealId, effectiveCustomNames);
		if (pricingBlockers.length > 0) {
			toast({
				title: `Cannot generate — the package estimate would not match the in-tool pricing. ${pricingBlockers.join('; ')}`,
				status: 'error',
				isClosable: true
			});
			return null;
		}

		if (!await pinPriceBooksForGeneration(effectiveDesignIds)) return null;

		const depositLinkWarning = resolveDepositLinkWarning(effectiveDealId);
		if (depositLinkWarning) toast({ title: depositLinkWarning, status: 'warning', duration: 9000, isClosable: true });

		setStep('capture');
		resetProgress();
		warmOptimizeWorker();

		try {
			grantProgress(1);
			beginProgressStep(PROGRESS_PROLOGUE - 1);

			// persist click-time state so the saved design matches the package — draft PUTs don't mint versions
			const dirtyControllers = effectiveDesignIds.flatMap((designId) => {
				const designState = designs.designs.find((d) => d.blueprint.id === designId);
				return designState?.isDirty && designState.bpController ? [designState.bpController] : [];
			});
			if (dirtyControllers.length > 0) {
				setCaptureStatus('Saving your designs...');
				const customerEmail = appNav.email;
				const saveResults = await Promise.all(dirtyControllers.map((bpController) => {
					const k = bpController.kitchen;
					// Draft the kitchen if it's a numbered version — the server rejects version≠null,baseVersion=null
					const kitchenDataOverride = k?.version !== null
						? { ...k.serialize(), version: null, baseVersion: k.version }
						: undefined;
					return saveCustomerBlueprint({
						controller: bpController as unknown as Controller,
						customerEmail,
						mode: 'live',
						getToken,
						kitchenDataOverride
					});
				}));
				if (saveResults.some(([, saveErr]) => saveErr !== null)) {
					toast({ title: 'Could not save your designs — presentation not generated', status: 'error', isClosable: true });
					setStep('select');
					return null;
				}
			}

			setCaptureStatus('Creating your presentation link...');
			const [created, createErr] = await createPresentation({
				customerEmail: appNav.email,
				contactId: customer.contactId,
				dealId: effectiveDealId,
				designIds: effectiveDesignIds,
				dealerNote: '',
				expiresAt: effectiveExpirationDate,
				getAccessTokenSilently: getToken
			});

			if (!created || createErr) {
				toast({ title: 'Failed to create presentation', status: 'error', isClosable: true });
				setStep('select');
				return null;
			}

			const { presentationId, link } = created;
			grantProgress(2);
			activePresentationIdRef.current = presentationId;
			setCreatedPackageId(presentationId);
			const totalSteps = effectiveDesignIds.length;

			// Pre-flight: capture the active design's live tdController BEFORE we
			// flip to Designer mode. The live controller already has its three
			// instance wired and all textures loaded.
			const activeDesignId = appNav.designId;
			const liveTdController = activeDesignId
				? (designs.designs.find((d) => d.blueprint.id === activeDesignId)?.tdController ?? null)
				: null;

			let liveGlb: ArrayBuffer | null = null;
			if (liveTdController) {
				try {
					setCaptureStatus('Capturing the current 3D view...');
					liveGlb = await liveTdController.exportGLB({ maxTextureSize: 2048 });
				} catch (err) {
					// Live export failed — will fall back to headless capture for active design
					console.warn('Live GLB pre-flight failed:', err instanceof Error ? err.message : err);
				}
			}
			grantProgress(3);

			// Apple Silicon WebGL constraint: only one <Kitchen3d> canvas can be active at a time.
			// Switch away from 3D mode before starting headless capture and wait for the canvas to unmount.
			const originalContentMode = contentMode;
			if (contentMode === ContentPanelMode.ThreeD) {
				setCaptureStatus('Preparing the 3D exporter...');
				dispatch(setContentPanelMode(ContentPanelMode.Designer));
				// Poll until liveTdController reports no renderer (canvas unmounted), capped at TEARDOWN_MAX_WAIT_MS
				if (liveTdController) {
					const deadline = Date.now() + TEARDOWN_MAX_WAIT_MS;
					while (Date.now() < deadline && liveTdController.rendererInfo !== null) {
						await sleep(TEARDOWN_POLL_INTERVAL_MS);
					}
				} else {
					await sleep(TEARDOWN_MAX_WAIT_MS);
				}
			}
			grantProgress(2);

			const designContexts = effectiveDesignIds.map((designId, i) => {
				const designState = designs.designs.find((d) => d.blueprint.id === designId);
				const blueprint = designState?.blueprint ?? null;
				const kitchen = designState?.bpController?.kitchen
					?? blueprint?.kitchens?.getActiveVersion()
					?? null;
				const customName = effectiveCustomNames[designId]?.trim() || (kitchen?.description ?? 'Untitled');
				return { index: i, designId, blueprint, kitchen, customName };
			});

			const outcomes = await runDesigns({
				presentationId,
				designContexts,
				activeDesignId: activeDesignId ?? null,
				liveGlb,
				effectiveDealId,
				totalSteps,
				progressOffset: 0
			});

			if (originalContentMode === ContentPanelMode.ThreeD) {
				dispatch(setContentPanelMode(ContentPanelMode.ThreeD));
			}

			setPresentationLink(link);

			const failed = outcomes.filter((o) => !o.glbOk || !o.uploadOk);
			if (failed.length > 0) {
				setFailedDesigns(failed);
				setStep('partial');
				const errorParts = failed.map((o) => `${o.designId}:${o.error ?? 'unknown'}`).join(', ');
				throw new Error(`partial-failure: ${failed.length}/${totalSteps} designs failed: ${errorParts}`);
			}

			setStep('success');
			return link;
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			if (message.startsWith('partial-failure:')) {
				// Already set step to 'partial' — rethrow for dev shim
				throw err;
			}
			toast({ title: 'Presentation generation failed', status: 'error', isClosable: true });
			setStep('select');
			return null;
		}
	};

	const handleRetryFailed = async (): Promise<void> => {
		const presentationId = activePresentationIdRef.current;
		if (!presentationId || failedDesigns.length === 0) return;

		const retryIds = failedDesigns.map((f) => f.designId);
		const effectiveDesignIds = retryIds;
		const effectiveCustomNames = customNames;
		const effectiveDealId = selectedDealId;
		const totalSteps = retryIds.length;

		setStep('capture');
		resetProgress();
		setCaptureStatus('Preparing the 3D exporter...');
		grantProgress(1);
		beginProgressStep(PROGRESS_PROLOGUE - 1);

		const activeDesignId = appNav.designId;
		const liveTdController = activeDesignId
			? (designs.designs.find((d) => d.blueprint.id === activeDesignId)?.tdController ?? null)
			: null;

		let liveGlb: ArrayBuffer | null = null;
		if (liveTdController) {
			try {
				liveGlb = await liveTdController.exportGLB({ maxTextureSize: 2048 });
			} catch {
				// fall through — headless path
			}
		}

		const originalContentMode = contentMode;
		if (contentMode === ContentPanelMode.ThreeD) {
			dispatch(setContentPanelMode(ContentPanelMode.Designer));
			if (liveTdController) {
				const deadline = Date.now() + TEARDOWN_MAX_WAIT_MS;
				while (Date.now() < deadline && liveTdController.rendererInfo !== null) {
					await sleep(TEARDOWN_POLL_INTERVAL_MS);
				}
			} else {
				await sleep(TEARDOWN_MAX_WAIT_MS);
			}
		}
		grantProgress(PROGRESS_PROLOGUE - 1);

		const designContexts = effectiveDesignIds.map((designId, i) => {
			const designState = designs.designs.find((d) => d.blueprint.id === designId);
			const blueprint = designState?.blueprint ?? null;
			const kitchen = designState?.bpController?.kitchen
				?? blueprint?.kitchens?.getActiveVersion()
				?? null;
			const customName = effectiveCustomNames[designId]?.trim() || (kitchen?.description ?? 'Untitled');
			return { index: i, designId, blueprint, kitchen, customName };
		});

		const outcomes = await runDesigns({
			presentationId,
			designContexts,
			activeDesignId: activeDesignId ?? null,
			liveGlb,
			effectiveDealId,
			totalSteps,
			progressOffset: 0
		});

		if (originalContentMode === ContentPanelMode.ThreeD) {
			dispatch(setContentPanelMode(ContentPanelMode.ThreeD));
		}

		const stillFailed = outcomes.filter((o) => !o.glbOk || !o.uploadOk);
		if (stillFailed.length > 0) {
			setFailedDesigns(stillFailed);
			setStep('partial');
		} else {
			setFailedDesigns([]);
			setStep('success');
		}
	};

	// Keep a ref pointed at the latest handleGenerate so the dev-bridge window
	// shim always invokes the closure with current state, not a stale render's copy.
	const handleGenerateRef = useRef(handleGenerate);
	handleGenerateRef.current = handleGenerate;
	const handleGenerateLinksRef = useRef(handleGenerateLinks);
	handleGenerateLinksRef.current = handleGenerateLinks;

	// Dev-only window shim: lets the dev-bridge MCP handler trigger generation
	// without driving the modal's DOM. Opens the modal first so the user can see
	// the run, then forwards overrides to the captured handleGenerate closure.
	// Stripped from production by Vite when `import.meta.env.DEV` is false.
	useEffect(() => {
		if (!import.meta.env.DEV) return;
		type DevBridgeGenerator = (overrides?: GenerateOverrides) => Promise<string | null>;
		type DevBridgeNs = { generatePresentation?: DevBridgeGenerator; generateDesignPackageLinks?: DevBridgeGenerator };
		const w = window as unknown as { __devBridge?: DevBridgeNs };
		w.__devBridge = w.__devBridge ?? {};
		// Yield once so the modal's open-effect can default the dealId before we
		// run the closure (the closure still resolves the dealId at call time).
		const openThen = async (run: DevBridgeGenerator, overrides?: GenerateOverrides): Promise<string | null> => {
			dispatch(setGeneratePresentationModal(true));
			await sleep(0);
			return run(overrides);
		};
		w.__devBridge.generatePresentation = (overrides) => openThen((o) => handleGenerateRef.current(o), overrides);
		w.__devBridge.generateDesignPackageLinks = (overrides) => openThen((o) => handleGenerateLinksRef.current(o), overrides);
		return () => {
			const ns = (window as unknown as { __devBridge?: DevBridgeNs }).__devBridge;
			if (ns) {
				delete ns.generatePresentation;
				delete ns.generateDesignPackageLinks;
			}
		};
	}, [dispatch]);

	const handleCopyLink = (link: string = presentationLink): void => {
		navigator.clipboard.writeText(link);
		toast({ title: lang.social.message.presentationLinkCopied, status: 'success', duration: 2000, isClosable: true });
	};

	const handleDeletePackage = async (): Promise<void> => {
		setDeleteConfirmOpen(false);
		if (!createdPackageId) return;

		setIsDeletingPackage(true);
		const [, failure] = await deleteDesignPackage({ packageId: createdPackageId, getAccessTokenSilently: getToken });
		setIsDeletingPackage(false);

		if (failure) {
			toast({ title: lang.social.message.deletePackageFailed, status: 'error', isClosable: true });
			return;
		}

		setIsPackageDeleted(true);
		toast({ title: lang.social.message.packageDeleted, status: 'success', isClosable: true });
	};

	return (
		<>
			{/*
			 * Capture portals must live OUTSIDE <Modal>. Chakra unmounts ModalContent
			 * when isOpen is false, which would prevent dev-bridge-triggered runs from
			 * mounting the headless Kitchen3d renderer until the modal animates open.
			 * The portals render `null` when idle, so this has no UX impact.
			 */}
			{CapturePortal}
			{CaptureOverheadPortal}
		<Modal isOpen={state.isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalCloseButton />

				{step === 'select' && (
					<>
						<ModalHeader>Generate Customer Presentation</ModalHeader>
						<ModalBody>
							<VStack align="stretch" spacing={4}>
								{project.length > 0 && (
									<VStack align="stretch" spacing={2}>
										<Text fontSize="sm" color="gray.600">
											Select the deal to associate this presentation with.
										</Text>
										<Stack spacing={2}>
											{project.map((deal) => {
												const isSelected = deal.dealId === selectedDealId;
												return (
													<HStack
														key={deal.dealId}
														p={2}
														borderWidth="1px"
														borderRadius="md"
														borderColor={isSelected ? 'blue.400' : 'gray.200'}
														bg={isSelected ? 'blue.50' : 'white'}
														cursor="pointer"
														_hover={{ borderColor: 'blue.300', bg: isSelected ? 'blue.50' : 'gray.50' }}
														onClick={() => setSelectedDealId(deal.dealId)}
														justify="space-between"
													>
														<VStack align="start" spacing={1}>
															<HStack spacing={2}>
																<Text fontSize="sm" fontWeight="bold">{deal.dealName || deal.dealId}</Text>
																<DepositLinkBadge depositLink={deal.depositLink} />
															</HStack>
															<Text fontSize="xs" color="gray.600">
																{[deal.address, deal.city, deal.state, deal.zip].filter(Boolean).join(', ')}
															</Text>
														</VStack>
														{isSelected && <Badge colorScheme="blue">Selected</Badge>}
													</HStack>
												);
											})}
										</Stack>
										{selectedDealDepositWarning && (
											<Alert status="warning" borderRadius="md" fontSize="xs" py={2}>
												<AlertIcon />
												{selectedDealDepositWarning}
											</Alert>
										)}
									</VStack>
								)}
								<Text fontSize="sm" color="gray.600">
									Select the designs to include in the presentation.
								</Text>
								<Stack spacing={2}>
									{sortedDesigns.map((d, i) => {
										const selectionIndex = selectedDesignIds.indexOf(d.blueprint.id);
										const isSelected = selectionIndex >= 0;
										return (
											<HStack key={d.blueprint.id} spacing={0} align="center">
												<Text
													fontSize="sm"
													color={isSelected ? 'blue.500' : 'gray.300'}
													fontWeight="600"
													minW="24px"
												>
													{isSelected ? `${selectionIndex + 1}.` : ''}
												</Text>
												<Checkbox
													isChecked={isSelected}
													onChange={(e) => setSelectedDesignIds((prev) =>
														e.target.checked
															? [...prev, d.blueprint.id]
															: prev.filter((id) => id !== d.blueprint.id)
													)}
												>
													{d.blueprint.kitchens.getActiveVersion()?.description ?? 'Untitled'}
												</Checkbox>
												<Input
													placeholder={`Design ${i + 1}`}
													size="sm"
													variant="flushed"
													maxW="180px"
													ml={3}
													value={customNames[d.blueprint.id] ?? ''}
													onChange={(e) => setCustomNames((prev) => ({ ...prev, [d.blueprint.id]: e.target.value }))}
												/>
											</HStack>
										);
									})}
								</Stack>
								<VStack align="stretch" spacing={2}>
									<Text fontSize="sm" color="gray.600">
										Select how long the presentation link stays active. The presentation data is deleted after it expires.
									</Text>
									<Select
										size="sm"
										maxW="200px"
										value={expirationKey}
										onChange={(e) => setExpirationKey(e.target.value)}
									>
										{EXPIRATION_PRESETS.filter((p) => !p.testOnly || isNonLive).map((p) => (
											<option key={p.key} value={p.key}>{p.label}</option>
										))}
									</Select>
								</VStack>
							</VStack>
						</ModalBody>
						<ModalFooter>
							<Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
							<Button
								colorScheme="blue"
								isDisabled={selectedDesignIds.length === 0}
								onClick={() => { void handleGenerateLinks(); }}
							>
								Generate
							</Button>
						</ModalFooter>
					</>
				)}

				{step === 'capture' && (
					<>
						<ModalHeader>Generating Presentation...</ModalHeader>
						<ModalBody>
							<VStack spacing={4} py={4}>
								<Progress
									value={captureProgress}
									width="100%"
									colorScheme="blue"
									borderRadius="md"
									hasStripe
									isAnimated
								/>
								<Text fontSize="sm" color="gray.600" data-testid="generation-status">{captureStatus}</Text>
							</VStack>
						</ModalBody>
					</>
				)}

				{step === 'success' && (
					<>
						<ModalHeader>{generatedLinks.length > 1 ? 'Share Links Ready' : 'Share Link Ready'}</ModalHeader>
						<ModalBody>
							<VStack align="stretch" spacing={4}>
								{isPackageDeleted
									? <Text fontSize="sm" color="red.600">This package has been deleted. The link below no longer works.</Text>
									: <Text fontSize="sm" color="gray.600">
										{generatedLinks.length > 1
											? 'Each design has its own link. Every link shows the design and its pricing as they stand when the customer opens it.'
											: 'Share this link with your customer. It shows the design and its pricing as they stand when the customer opens it.'}
									</Text>}
								{(generatedLinks.length > 0 ? generatedLinks : [{ designId: 'link', name: '', link: presentationLink }]).map((entry) => (
									<VStack key={entry.designId} align="stretch" spacing={1}>
										{entry.name && <Text fontSize="xs" fontWeight="semibold" color="gray.600">{entry.name}</Text>}
										<InputGroup>
											<Input value={entry.link} isReadOnly pr="9rem" fontSize="sm" />
											<InputRightElement width="9rem">
												<Button h="1.75rem" size="sm" mr={1} isDisabled={isPackageDeleted} onClick={() => handleCopyLink(entry.link)} leftIcon={<Copy size={14} />}>
													Copy
												</Button>
												<Button h="1.75rem" size="sm" isDisabled={isPackageDeleted} onClick={() => window.open(entry.link, '_blank')} leftIcon={<ExternalLink size={14} />}>
													Open
												</Button>
											</InputRightElement>
										</InputGroup>
									</VStack>
								))}
							</VStack>
						</ModalBody>
						<ModalFooter justifyContent={createdPackageId && !isPackageDeleted ? 'space-between' : 'flex-end'}>
							{createdPackageId && !isPackageDeleted
								? <Button
									variant="outline"
									colorScheme="red"
									leftIcon={<Trash2 size={14} />}
									isLoading={isDeletingPackage}
									onClick={() => setDeleteConfirmOpen(true)}
								>
									Delete package
								</Button>
								: null}
							<Button colorScheme="blue" onClick={onClose}>Done</Button>
						</ModalFooter>
					</>
				)}

				{step === 'partial' && (
					<>
						<ModalHeader>Presentation Partially Ready</ModalHeader>
						<ModalBody>
							<VStack align="stretch" spacing={4}>
								<Text fontSize="sm" color="gray.600">
									The presentation was created, but some designs had errors. You can share the link now or retry the failed designs.
								</Text>
								<InputGroup>
									<Input value={presentationLink} isReadOnly pr="9rem" fontSize="sm" />
									<InputRightElement width="9rem">
										<Button h="1.75rem" size="sm" mr={1} onClick={() => handleCopyLink()} leftIcon={<Copy size={14} />}>
											Copy
										</Button>
										<Button h="1.75rem" size="sm" onClick={() => window.open(presentationLink, '_blank')} leftIcon={<ExternalLink size={14} />}>
											Open
										</Button>
									</InputRightElement>
								</InputGroup>
								<VStack align="stretch" spacing={1}>
									<Text fontSize="sm" fontWeight="semibold" color="red.600">
										Failed designs ({failedDesigns.length}):
									</Text>
									{failedDesigns.map((f) => (
										<Text key={f.designId} fontSize="xs" color="red.500">
											{f.name}: {f.error ?? 'unknown error'}
										</Text>
									))}
								</VStack>
							</VStack>
						</ModalBody>
						<ModalFooter justifyContent={createdPackageId && !isPackageDeleted ? 'space-between' : 'flex-end'}>
							{createdPackageId && !isPackageDeleted
								? <Button
									variant="outline"
									colorScheme="red"
									leftIcon={<Trash2 size={14} />}
									isLoading={isDeletingPackage}
									onClick={() => setDeleteConfirmOpen(true)}
								>
									Delete package
								</Button>
								: null}
							<HStack>
								<Button variant="ghost" onClick={onClose}>Close</Button>
								<Button colorScheme="orange" onClick={() => { void handleRetryFailed(); }}>
									Retry failed designs
								</Button>
							</HStack>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>

		<Modal isOpen={isDeleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
			<ModalOverlay />
			<ModalContent>
				<ModalCloseButton />
				<ModalHeader>Delete this design package?</ModalHeader>
				<ModalBody>
					<Text fontSize="sm" color="gray.600">{lang.social.message.deletePackageWarning}</Text>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
					<Button colorScheme="red" onClick={() => { void handleDeletePackage(); }}>Delete package</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
		</>
	);
};
